/**
 * Iraqi mobile numbers: normalize user input to E.164 (`DIALING_CODE_IRAQ` + 10-digit national).
 * National validation uses `INVALID_PHONE_NUMBER_REGEX` (aligned with login and checkout).
 */

import { DIALING_CODE_IRAQ, INVALID_PHONE_NUMBER_REGEX } from './constants';

const IRAQ_CALLING_CODE_DIGITS = DIALING_CODE_IRAQ.replace('+', '');
const IRAQI_MOBILE_NATIONAL_DIGIT_LENGTH = 10;

/** Machine-readable failure from `normalizeIraqiPhone` when `ok` is false. */
export type NormalizeIraqiPhoneErrorCode =
  | 'empty'
  | 'invalid_digits'
  | 'invalid_length'
  | 'invalid_format';

/**
 * Discriminated result: both canonical forms of the same number on success, or `error` on failure.
 *
 * Normalization derives the national digits on the way to building the E.164 string, so it hands
 * back both instead of making callers parse one out of the other: `e164` is what we store and
 * submit, `national` is what an input bound to a `+964` prefix element holds.
 */
export type NormalizeIraqiPhoneResult =
  | { ok: true; e164: string; national: string }
  | { ok: false; error: NormalizeIraqiPhoneErrorCode };

/**
 * Default react-intl message descriptors for Iraqi phone validation.
 * Keep `defaultMessage` in sync with `translations/en.json` for each `id`.
 */
export const IRAQI_PHONE_INTL_MESSAGES = {
  loginInvalid: {
    id: 'loginModal.invalid',
    defaultMessage: 'Invalid phone number',
  },
  checkoutInvalid: {
    id: 'checkout.phoneNumber.invalid',
    defaultMessage: 'Please enter a valid Iraqi phone number (7XXXXXXXXX)',
  },
  checkoutFieldRequired: {
    id: 'checkout.fieldIsRequired',
    defaultMessage: 'Field is required',
  },
} as const;

/**
 * Maps a `normalizeIraqiPhone` error code to a react-intl descriptor for `formatMessage`.
 *
 * @param error - Result from a failed normalization (`ok: false`).
 * @param uiContext - `login` uses a single invalid message for all non-empty errors; `checkout` uses
 *   required vs invalid phone messages (matches checkout form copy).
 * @returns `{ id, defaultMessage }` suitable for `intl.formatMessage(...)`.
 */
export const getNormalizeIraqiPhoneIntlMessage = (
  error: NormalizeIraqiPhoneErrorCode,
  uiContext: 'login' | 'checkout' | 'b2b',
): { id: string; defaultMessage: string } => {
  if (error === 'empty') {
    return uiContext === 'login'
      ? { ...IRAQI_PHONE_INTL_MESSAGES.loginInvalid }
      : { ...IRAQI_PHONE_INTL_MESSAGES.checkoutFieldRequired };
  }
  return uiContext === 'login'
    ? { ...IRAQI_PHONE_INTL_MESSAGES.loginInvalid }
    : { ...IRAQI_PHONE_INTL_MESSAGES.checkoutInvalid };
};

/**
 * Display helper: stored E.164 (`+964` + 10 digits) → common local prefix (`07…`).
 * If `e164` is not a valid Iraqi E.164, returns the trimmed string as-is (or empty).
 */
export const e164IraqiToDisplayNational = (e164: string): string => {
  const trimmed = e164?.trim() ?? '';
  if (!trimmed.startsWith(DIALING_CODE_IRAQ)) {
    return trimmed;
  }
  const nsn = trimmed.slice(DIALING_CODE_IRAQ.length);
  if (nsn.length !== 10 || !/^\d{10}$/.test(nsn)) {
    return trimmed;
  }
  return `0${nsn}`;
};

/**
 * Maps Arabic-Indic (U+0660–0669) and Extended Arabic-Indic / Persian (U+06F0–06F9) digits onto
 * ASCII `0-9`, leaving everything else untouched.
 *
 * Needed because `extractDigits` filters on `\D`, which treats those code points as non-digits
 * and would silently delete them — an Arabic-locale contact saved as `٧٧٠١٢٣٤٥٦٧` would come
 * back empty rather than as a phone number.
 */
const asciifyArabicIndicDigits = (value: string) =>
  value.replace(/[٠-٩۰-۹]/g, (digit) => {
    const base = digit >= '۰' ? 0x06f0 : 0x0660;
    return String(digit.charCodeAt(0) - base);
  });

/** Returns only ASCII digits from `value` (strips spaces, `+`, punctuation, etc.). */
const extractDigits = (value: string) => asciifyArabicIndicDigits(value).replace(/\D/g, '');

/**
 * Removes repeated leading `00` international access prefixes from a digit-only string.
 * Stops when fewer than three digits remain so a short input is not over-stripped.
 */
const stripLeading00AccessPrefix = (digits: string) => {
  let rest = digits;
  while (rest.startsWith('00') && rest.length > 2) {
    rest = rest.slice(2);
  }
  return rest;
};

/**
 * Parses raw user input into the 10-digit Iraqi national mobile number (NSN).
 *
 * Accepts: leading `0`, bare 10 digits, `964…`, `+964…` (after digit extraction), and `00`-prefixed
 * international dial. Returns `null` if there are no digits at all after normalization.
 *
 * @param rawInput - Trimmed or untrimmed string from an input field.
 * @returns National digits only (e.g. `7701234567`), or `null` if no digits were found.
 */
const extractNationalMobileDigits = (rawInput: string): string | null => {
  const digits = stripLeading00AccessPrefix(extractDigits(rawInput));
  if (!digits.length) return null;
  if (digits.startsWith(IRAQ_CALLING_CODE_DIGITS)) {
    return digits.slice(IRAQ_CALLING_CODE_DIGITS.length) || null;
  }
  if (digits.startsWith('0')) return digits.slice(1) || null;
  return digits;
};

/**
 * Whether `nationalDigits` matches the same national pattern as login/checkout (`7[56789]` + 8 digits).
 */
const matchesLoginCheckoutNationalRegex = (nationalDigits: string) =>
  INVALID_PHONE_NUMBER_REGEX.test(nationalDigits.trim());

/**
 * Normalizes Iraqi mobile input to canonical E.164: `DIALING_CODE_IRAQ` + valid 10-digit national.
 *
 * **Intended inputs (after digit extraction):** domestic with `0`, without `0`, `964…`, `+964…`
 * (non-digits removed), optional `00` international prefix, optional spaces/punctuation, and
 * Arabic-Indic / Persian digits (`٧٧٠…`, `۷۷۰…`).
 *
 * Also the single entry point for phone numbers arriving from a **contact picker** (device or
 * saved holders), which is why over-length input is rejected rather than truncated: trimming
 * `77012345678` down to 10 digits would produce a different, valid number belonging to someone
 * else, and checkout delivers tickets over WhatsApp.
 *
 * @param input - Raw phone string from the user, or from a picked contact.
 * @returns On success, `{ ok: true, e164, national }` — e.g. `+9647701234567` and `7701234567`.
 *   On failure, `{ ok: false, error }` with a stable machine-readable code for UI and logging.
 */
export const normalizeIraqiPhone = (input: string): NormalizeIraqiPhoneResult => {
  const trimmedInput = input.trim();
  if (!trimmedInput.length) return { ok: false, error: 'empty' };

  const nationalDigits = extractNationalMobileDigits(trimmedInput);
  if (nationalDigits === null) return { ok: false, error: 'invalid_digits' };
  if (nationalDigits.length !== IRAQI_MOBILE_NATIONAL_DIGIT_LENGTH) {
    return { ok: false, error: 'invalid_length' };
  }
  if (!matchesLoginCheckoutNationalRegex(nationalDigits)) {
    return { ok: false, error: 'invalid_format' };
  }

  return {
    ok: true,
    e164: `${DIALING_CODE_IRAQ}${nationalDigits}`,
    national: nationalDigits,
  };
};
